import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { organisationAdminService } from '../services/organisationAdminService'
import { PageHeader } from '../components/ui/PageHeader'
import { DataTable } from '../components/ui/DataTable'
import { Spinner } from '../components/ui/Spinner'
import { Pagination } from '../components/ui/Pagination'

function formatDateTime(isoOrDate) {
  if (!isoOrDate) return '—'
  const d = new Date(isoOrDate)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function workflowTitle(workflow) {
  if (!workflow) return '—'
  if (workflow.source === 'PRODUCT') {
    return workflow.product_config_name || workflow.display_company_name || 'Product discovery'
  }
  return workflow.workflow_name || workflow.display_company_name || workflow.company_name || 'ICP workflow'
}

const WORKFLOWS_PAGE_SIZE = 25

const OrganisationDetail = () => {
  const { organisationId } = useParams()
  const [organisation, setOrganisation] = useState(null)
  const [users, setUsers] = useState([])
  const [workflowConfigs, setWorkflowConfigs] = useState([])
  const [workflows, setWorkflows] = useState([])
  const [workflowsPage, setWorkflowsPage] = useState(1)
  const [workflowsPagination, setWorkflowsPagination] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const fetchDetail = useCallback(async () => {
    setLoading(true)
    setNotFound(false)
    setOrganisation(null)
    setUsers([])
    setWorkflowConfigs([])
    try {
      const orgRes = await organisationAdminService.getOrganisationById(organisationId)
      const org = orgRes?.data ?? null
      if (!org) {
        setNotFound(true)
        return
      }
      setOrganisation(org)
      const [usersRes, configsRes] = await Promise.all([
        organisationAdminService.getOrganisationUsers(organisationId),
        organisationAdminService.listOrganisationWorkflowConfigs(organisationId),
      ])
      setUsers(Array.isArray(usersRes?.data) ? usersRes.data : [])
      setWorkflowConfigs(Array.isArray(configsRes?.data) ? configsRes.data : [])
    } catch {
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }, [organisationId])

  const fetchWorkflows = useCallback(async () => {
    try {
      const workflowsRes = await organisationAdminService.listOrganisationWorkflows(organisationId, {
        page: workflowsPage,
        limit: WORKFLOWS_PAGE_SIZE,
      })
      const workflowPayload = workflowsRes?.data
      setWorkflows(Array.isArray(workflowPayload?.workflows) ? workflowPayload.workflows : [])
      setWorkflowsPagination(workflowPayload?.pagination ?? null)
    } catch {
      setWorkflows([])
      setWorkflowsPagination(null)
    }
  }, [organisationId, workflowsPage])

  useEffect(() => {
    setWorkflowsPage(1)
  }, [organisationId])

  useEffect(() => {
    fetchDetail()
  }, [fetchDetail])

  useEffect(() => {
    if (!organisation) return
    fetchWorkflows()
  }, [organisation, fetchWorkflows])

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    )
  }

  if (notFound || !organisation) {
    return (
      <div>
        <PageHeader
          title="Organisation not found"
          breadcrumbs={[{ label: 'Organisations', href: '/admin/organisations' }, { label: 'Not found' }]}
        />
        <div className="card text-text-muted">This organisation could not be loaded.</div>
      </div>
    )
  }

  const userColumns = [
    { key: 'fullname', header: 'Name', render: (u) => u.fullname || '—' },
    { key: 'email', header: 'Email' },
    { key: 'current_user_region', header: 'Region', render: (u) => u.current_user_region || '—' },
    { key: 'region_currency', header: 'Currency', render: (u) => u.region_currency || '—' },
    { key: 'created_at', header: 'Joined', render: (u) => formatDateTime(u.created_at) },
  ]

  const configColumns = [
    { key: 'name', header: 'Name', render: (c) => c.name || '—' },
    { key: 'company_name', header: 'Company', render: (c) => c.company_name || '—' },
    {
      key: 'domains',
      header: 'Domains',
      render: (c) => (Array.isArray(c.domains) ? c.domains.length : 0),
    },
    {
      key: 'minimum_score',
      header: 'Min score',
      render: (c) => (c.minimum_score != null ? c.minimum_score : 70),
    },
    {
      key: 'updated_at',
      header: 'Updated',
      render: (c) => formatDateTime(c.updated_at || c.created_at),
    },
    {
      key: 'view',
      header: '',
      render: (c) => (
        <Link
          to={`/admin/organisations/${organisationId}/workflow-configs/${c.workflow_config_id}`}
          className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
        >
          View
        </Link>
      ),
    },
  ]

  const workflowColumns = [
    {
      key: 'name',
      header: 'Name',
      render: (w) => workflowTitle(w),
    },
    {
      key: 'source',
      header: 'Source',
      render: (w) => (w.source === 'PRODUCT' ? 'Product' : 'Workflow'),
    },
    { key: 'status', header: 'Status', render: (w) => w.status || '—' },
    { key: 'started_at', header: 'Started', render: (w) => formatDateTime(w.started_at) },
    {
      key: 'leads',
      header: 'Leads',
      render: (w) => w.actual_leads_count ?? w.display_leads_count ?? '—',
    },
    {
      key: 'view',
      header: '',
      render: (w) => (
        <Link
          to={`/admin/organisations/${organisationId}/workflows/${w.workflow_id}`}
          className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
        >
          View leads
        </Link>
      ),
    },
  ]

  const workflowPagination = workflowsPagination
    ? {
        currentPage: workflowsPagination.current_page,
        totalPages: workflowsPagination.total_pages,
        totalItems: workflowsPagination.total_count ?? 0,
        hasPrevPage: workflowsPagination.has_prev_page,
        hasNextPage: workflowsPagination.has_next_page,
      }
    : null

  return (
    <div>
      <PageHeader
        title={organisation.organisation_name}
        subtitle="Organisation details, members, workflows, and configs"
        breadcrumbs={[
          { label: 'Organisations', href: '/admin/organisations' },
          { label: organisation.organisation_name },
        ]}
      />

      <div className="card mb-8">
        <h2 className="mb-4 text-lg font-semibold text-secondary-900 dark:text-secondary-50">Details</h2>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="font-medium text-text-muted">Organisation ID</dt>
            <dd className="mt-1 break-all font-mono text-secondary-900 dark:text-secondary-100">{organisation.organisation_id}</dd>
          </div>
          <div>
            <dt className="font-medium text-text-muted">Website</dt>
            <dd className="mt-1">
              {organisation.website ? (
                <a
                  href={
                    organisation.website.startsWith('http')
                      ? organisation.website
                      : `https://${organisation.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline dark:text-primary-400"
                >
                  {organisation.website}
                </a>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-text-muted">Subscription</dt>
            <dd className="mt-1">
              {organisation.subscription_name
                ? `${organisation.subscription_name} (${organisation.subscription_code || '—'})`
                : organisation.subscription_code || '—'}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-gray-500">Created</dt>
            <dd className="mt-1 text-gray-900">{formatDateTime(organisation.created_at)}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-500">Updated</dt>
            <dd className="mt-1 text-gray-900">{formatDateTime(organisation.updated_at)}</dd>
          </div>
        </dl>

        {Array.isArray(organisation.locations) && organisation.locations.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Locations</h3>
            <ul className="space-y-2">
              {organisation.locations.map((loc) => (
                <li key={loc.location_id || `${loc.address}-${loc.city}`} className="text-sm text-gray-800">
                  {[loc.address, loc.city, loc.state, loc.country].filter(Boolean).join(', ')}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <h2 className="mb-3 text-lg font-semibold">Users ({users.length})</h2>
      <DataTable
        columns={userColumns}
        data={users}
        emptyTitle="No users in this organisation"
        getRowKey={(u) => u.user_id}
        className="mb-8"
      />

      <h2 className="mb-3 text-lg font-semibold">Workflow configs ({workflowConfigs.length})</h2>
      <DataTable
        columns={configColumns}
        data={workflowConfigs}
        emptyTitle="No workflow configs"
        getRowKey={(c) => c.workflow_config_id}
        className="mb-8"
      />

      <h2 className="mb-3 text-lg font-semibold">
        Workflows{workflowsPagination?.total_count != null ? ` (${workflowsPagination.total_count})` : ''}
      </h2>
      {workflowPagination && workflowPagination.totalPages > 1 && (
        <Pagination
          className="mb-3"
          pagination={workflowPagination}
          onPrev={() => setWorkflowsPage((p) => Math.max(1, p - 1))}
          onNext={() => setWorkflowsPage((p) => p + 1)}
        />
      )}
      <DataTable
        columns={workflowColumns}
        data={workflows}
        emptyTitle="No workflows"
        minWidth="min-w-[800px]"
        getRowKey={(w) => w.workflow_id}
      />
    </div>
  )
}

export default OrganisationDetail
